import { useContext } from 'react';
import { GlobalDataContext, TemplateContext, evaluateTemplateValue } from '@ea-lab/reactive-json';
import { PriorityGlyph } from '../shared/fields/priority-icon';
import { getTagColorClass } from '../../utils/tagColors';

/**
 * Display-only reactive-json elements for the Elasticsearch tasks list
 * (/tasks). The Kanban plugins (KanbanPriorityIcon/UserAvatar/TagList) are
 * INTERACTIVE (write into apiData) and expect the Kanban card shape (ids +
 * options) — unusable on a read-only ES row that only carries labels/names.
 * These mirror the SAME colour logic (PriorityGlyph, tagColors, the DS task-type
 * tones) so /tasks reads like the Kanban list view, but render nothing editable.
 *
 * Toutes les couleurs viennent du DS (tokens slate/rose/emerald/amber/sky/iris) ;
 * aucune couleur ad-hoc.
 */

const ev = (gdc, tc) => (v) => {
  try {
    return evaluateTemplateValue({ valueToEvaluate: v, globalDataContext: gdc, templateContext: tc });
  } catch {
    return undefined;
  }
};

const TONE_CLASS = {
  rose:    'bg-rose-100 text-rose-700 border-rose-200',
  emerald: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  amber:   'bg-amber-100 text-amber-800 border-amber-200',
  sky:     'bg-sky-100 text-sky-700 border-sky-200',
  iris:    'bg-iris-100 text-iris-700 border-iris-200',
  slate:   'bg-slate-100 text-slate-600 border-slate-200',
};

// Type → ton DS (cf. DS « Cosmos » + TaskTypeBadge.getBadgeClass).
const typeTone = (label = '') => {
  const l = String(label).toLowerCase();
  if (l.includes('bug')) return 'rose';
  if (l.includes('évolu') || l.includes('evolu') || l.includes('préventive') || l.includes('preventive') || l === 'mep' || l.includes('mep')) return 'emerald';
  if (l.includes('sous-tâche') || l.includes('sous-tache') || l.includes('sub-task') || l.includes('subtask')) return 'amber';
  if (l.includes('retour')) return 'sky';
  return 'sky';
};

// État (machine name) → ton DS par mot-clé (robuste sans énumérer tous les états).
const stateTone = (state = '') => {
  const s = String(state).toLowerCase();
  if (s.includes('block') || s.includes('cancel') || s.includes('annul') || s.includes('refus')) return 'rose';
  if (s.includes('done') || s.includes('termin') || s.includes('clos') || s.includes('validate_customer') || s.includes('valide')) return 'emerald';
  if (s.includes('valid') || s.includes('review') || s.includes('recett') || s.includes('verif')) return 'amber';
  if (s.includes('backlog') || s.includes('todo') || s.includes('a_faire') || s.includes('demande')) return 'slate';
  return 'sky'; // en cours / prêt / dev
};

// done_producer → « Done producer ».
const prettifyState = (s = '') => {
  const t = String(s).replace(/_/g, ' ').trim();
  return t ? t.charAt(0).toUpperCase() + t.slice(1) : t;
};

const PRIORITY_FR = { 1: 'Très basse', 2: 'Basse', 3: 'Moyenne', 4: 'Haute', 5: 'Critique' };

const initials = (name = '') => {
  const parts = String(name).split(/[.\s_-]+/).filter(Boolean);
  const ini = parts.slice(0, 2).map((p) => p[0]).join('') || String(name).charAt(0) || '?';
  return ini.toUpperCase();
};

/** Badge coloré pour le TYPE ou l'ÉTAT d'une tâche. props: { value, kind }. */
export function TaskColorBadge({ props = {} }) {
  const e = ev(useContext(GlobalDataContext), useContext(TemplateContext));
  const value = e(props.value);
  if (value == null || value === '') return null;
  const kind = e(props.kind) || 'type';
  const tone = kind === 'state' ? stateTone(value) : typeTone(value);
  const label = kind === 'state' ? prettifyState(value) : value;
  const base = kind === 'state'
    ? 'inline-flex px-2 py-0.5 rounded text-xs font-semibold border whitespace-nowrap'
    : 'inline-flex items-center text-[10px] uppercase font-bold tracking-wide px-1.5 py-0.5 rounded border whitespace-nowrap';
  return <span className={`${base} ${TONE_CLASS[tone]}`}>{label}</span>;
}

/** Icône de priorité colorée (réutilise PriorityGlyph du kanban) + libellé FR. props: { value }. */
export function TaskPriorityIcon({ props = {} }) {
  const e = ev(useContext(GlobalDataContext), useContext(TemplateContext));
  const level = Number(e(props.value)) || 0;
  if (!level) return <span className="text-xs text-slate-400">—</span>;
  return (
    <span className="inline-flex items-center gap-1.5">
      <PriorityGlyph level={level} size={16} />
      <span className="text-xs text-slate-600">{PRIORITY_FR[level] || ''}</span>
    </span>
  );
}

/** Avatar à initiales (recette .avatar DS) + nom. props: { name }. */
export function TaskAssigneeAvatar({ props = {} }) {
  const e = ev(useContext(GlobalDataContext), useContext(TemplateContext));
  const name = e(props.name);
  if (!name) return <span className="text-xs text-slate-400">—</span>;
  return (
    <span className="inline-flex items-center gap-2 min-w-0">
      <span className="avatar avatar-xs" aria-hidden="true">{initials(name)}</span>
      <span className="text-xs text-slate-600 truncate">{name}</span>
    </span>
  );
}

/** Tags colorés en ligne (réutilise tagColors). props: { value } (string|string[]). */
export function TaskTagsInline({ props = {} }) {
  const e = ev(useContext(GlobalDataContext), useContext(TemplateContext));
  const raw = e(props.value);
  const list = Array.isArray(raw) ? raw : (raw ? [raw] : []);
  if (!list.length) return null;
  return (
    <span className="flex flex-wrap items-center gap-1 mt-1">
      {list.map((t, i) => (
        <span key={i} className={`inline-flex items-center text-[10px] font-medium px-1.5 py-0.5 rounded-full border ${getTagColorClass(t)}`}>{t}</span>
      ))}
    </span>
  );
}
